import { useState, useEffect, useCallback } from 'react'

type NotificationPermission = 'default' | 'denied' | 'granted'

interface UseBrowserNotificationsReturn {
  permission: NotificationPermission
  isSupported: boolean
  requestPermission: () => Promise<boolean>
  sendNotification: (title: string, options?: NotificationOptions) => Notification | null
}

/**
 * Hook to manage browser notifications
 */
export function useBrowserNotifications(): UseBrowserNotificationsReturn {
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const isSupported = typeof window !== 'undefined' && 'Notification' in window

  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission)
    }
  }, [isSupported])

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false

    try {
      const result = await Notification.requestPermission()
      setPermission(result)
      return result === 'granted'
    } catch (error) {
      console.error('Failed to request notification permission:', error)
      return false
    }
  }, [isSupported])

  const sendNotification = useCallback(
    (title: string, options?: NotificationOptions): Notification | null => {
      if (!isSupported || permission !== 'granted') {
        return null
      }

      try {
        const notification = new Notification(title, {
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          ...options,
        })

        // Auto-close after 5 seconds
        setTimeout(() => notification.close(), 5000)

        return notification
      } catch (error) {
        console.error('Failed to send notification:', error)
        return null
      }
    },
    [isSupported, permission]
  )

  return {
    permission,
    isSupported,
    requestPermission,
    sendNotification,
  }
}

/**
 * Storage key for notification preference
 */
const NOTIFICATION_PREF_KEY = 'cenva_browser_notifications'

/**
 * Hook to manage notification preferences with localStorage persistence
 */
export function useNotificationPreference() {
  const [enabled, setEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      const stored = localStorage.getItem(NOTIFICATION_PREF_KEY)
      return stored === 'true'
    } catch {
      return false
    }
  })

  const updatePreference = useCallback((value: boolean) => {
    setEnabled(value)
    try {
      localStorage.setItem(NOTIFICATION_PREF_KEY, String(value))
    } catch {
      // Ignore storage errors
    }
  }, [])

  return { enabled, setEnabled: updatePreference }
}
